import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  StorageWorkerInitPayload,
  StorageWorkerMarkEnvSourcePayload,
  StorageWorkerMessage,
  StorageWorkerPutConfigPayload,
  StorageWorkerPutFlowsPayload,
  StorageWorkerProbeResult,
  StorageWorkerRequest,
  StorageWorkerResponse,
  StorageWorkerLoadSearchDefinitionsPayload,
  StorageWorkerSearchDefinitionResult,
  StorageWorkerLoadFlowOwnersPayload,
  StorageWorkerFlowOwnersResult,
  StorageWorkerLoadFlowRunsPayload,
  StorageWorkerFlowRunsResult,
  StorageWorkerCancelScanPayload,
  StorageWorkerScanAcceptedResult,
  StorageWorkerScanAdminFlowsPayload,
  StorageWorkerScanFlowsPayload,
  StorageWorkerScanProgressPayload,
  StorageWorkerScanRunsPayload,
} from './storage-worker.protocol';
import { ConfigurationItem } from './storage-types';
import { EnvScanSource, StoredEnvironment, StoreStats } from './storage-backend';

type PendingRequest = {
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
  timeout: any;
};

@Injectable({
  providedIn: 'root',
})
export class StorageWorkerClientService implements OnDestroy {
  private worker: Worker | null = null;
  private requestId = 0;
  private readonly pending = new Map<string, PendingRequest>();
  private probePromise: Promise<StorageWorkerProbeResult | null> | null = null;
  private readonly scanProgressSubject = new Subject<StorageWorkerScanProgressPayload>();

  get scanProgress$(): Observable<StorageWorkerScanProgressPayload> {
    return this.scanProgressSubject.asObservable();
  }

  createScanId(prefix = 'scan'): string {
    return `${prefix}-${Date.now()}-${++this.requestId}`;
  }

  scanProgressFor(scanId: string): Observable<StorageWorkerScanProgressPayload> {
    return this.scanProgress$.pipe(filter((event) => event?.scanId === scanId));
  }

  async probe(): Promise<StorageWorkerProbeResult | null> {
    if (this.probePromise) {
      return this.probePromise;
    }

    this.probePromise = this.probeInternal();
    return this.probePromise;
  }

  ngOnDestroy(): void {
    this.shutdown();
  }

  shutdown(): void {
    for (const [, pending] of this.pending) {
      clearTimeout(pending.timeout);
      pending.reject(new Error('Storage worker shut down'));
    }
    this.pending.clear();
    this.worker?.terminate();
    this.worker = null;
    this.probePromise = null;
  }

  init(payload: StorageWorkerInitPayload): Promise<{ mode: 'worker-opfs'; sqliteOwner: true }> {
    return this.request('init', payload, 15000);
  }

  getMode(): Promise<'worker-opfs'> {
    return this.request('getMode');
  }

  flush(): Promise<true> {
    return this.request('flush');
  }

  getConfig(key: string): Promise<ConfigurationItem> {
    return this.request('getConfig', { key });
  }

  putConfig(key: string, item: ConfigurationItem): Promise<true> {
    const payload: StorageWorkerPutConfigPayload = {
      key,
      item: {
        key,
        value: item?.value ?? null,
      },
    };
    return this.request('putConfig', payload);
  }

  getAllConfigs(): Promise<{ key: string; value: any }[]> {
    return this.request('getAllConfigs');
  }

  getAllUserAliases(): Promise<{ email: string; user_key: string }[]> {
    return this.request('getAllUserAliases');
  }

  getStoreStats(): Promise<StoreStats> {
    return this.request('getStoreStats');
  }

  getEnvironment(name: string): Promise<StoredEnvironment | null> {
    return this.request('getEnvironment', { name });
  }

  getEnvironments(): Promise<StoredEnvironment[]> {
    return this.request('getEnvironments');
  }

  putEnvironment(env: any): Promise<true> {
    return this.request('putEnvironment', { env });
  }

  putEnvironments(envs: any[]): Promise<true> {
    return this.request('putEnvironments', { envs });
  }

  markEnvScanCompleted(name: string, flowCount: number, durationMs: number): Promise<true> {
    return this.request('markEnvScanCompleted', { name, flowCount, durationMs });
  }

  markEnvSourceScanned(name: string, source: EnvScanSource, count?: number, durationMs?: number): Promise<true> {
    const payload: StorageWorkerMarkEnvSourcePayload = { name, source, flowCount: count, durationMs };
    return this.request('markEnvSourceScanned', payload);
  }

  getFlows(): Promise<any[]> {
    return this.request('getFlows');
  }

  getFlowSummaries(environment?: string): Promise<any[]> {
    return this.request('getFlowSummaries', { environment });
  }

  getFlowsAdmin(): Promise<any[]> {
    return this.request('getFlowsAdmin');
  }

  putFlow(flow: any, admin = false, options?: { persist?: boolean }): Promise<true> {
    return this.request('putFlow', { flow, admin, options });
  }

  putFlows(flows: any[], admin = false, options?: { persist?: boolean }): Promise<true> {
    const payload: StorageWorkerPutFlowsPayload = { flows, admin, options };
    return this.request('putFlows', payload);
  }

  removeFlows(flows: any[], admin = false): Promise<true> {
    return this.request('removeFlows', { flows, admin });
  }

  markRunScanQueued(environment: string, flowNames: string[], reason?: string, error?: string): Promise<true> {
    return this.request('markRunScanQueued', { environment, flowNames, reason, error });
  }

  clearRunScanQueued(environment: string, flowNames: string[]): Promise<true> {
    return this.request('clearRunScanQueued', { environment, flowNames });
  }

  getUsers(): Promise<any[]> {
    return this.request('getUsers');
  }

  putUsers(users: any[]): Promise<true> {
    return this.request('putUsers', { users });
  }

  getUserByEmail(email: string): Promise<any | undefined> {
    return this.request('getUserByEmail', { email });
  }

  getRuns(environment: string | null = null): Promise<any[]> {
    return this.request('getRuns', { environment });
  }

  putRuns(runs: any[]): Promise<true> {
    return this.request('putRuns', { runs });
  }

  getConnections(environment?: string): Promise<any[]> {
    return this.request('getConnections', { environment });
  }

  putConnections(connections: any[], environment: string, scope: 'user' | 'admin'): Promise<true> {
    return this.request('putConnections', { connections, environment, scope });
  }

  scanFlows(payload: StorageWorkerScanFlowsPayload): Promise<StorageWorkerScanAcceptedResult> {
    return this.request('scanFlows', payload, 180000);
  }

  scanAdminFlows(payload: StorageWorkerScanAdminFlowsPayload): Promise<StorageWorkerScanAcceptedResult> {
    return this.request('scanAdminFlows', payload, 180000);
  }

  scanRuns(payload: StorageWorkerScanRunsPayload): Promise<StorageWorkerScanAcceptedResult> {
    return this.request('scanRuns', payload, 15000);
  }

  cancelScan(payload: StorageWorkerCancelScanPayload): Promise<StorageWorkerScanAcceptedResult> {
    return this.request('cancelScan', payload, 5000);
  }

  loadSearchDefinitions(payload: StorageWorkerLoadSearchDefinitionsPayload): Promise<StorageWorkerSearchDefinitionResult[]> {
    return this.request('loadSearchDefinitions', payload, 60000);
  }

  loadFlowOwners(payload: StorageWorkerLoadFlowOwnersPayload): Promise<StorageWorkerFlowOwnersResult[]> {
    return this.request('loadFlowOwners', payload, 60000);
  }

  loadFlowRuns(payload: StorageWorkerLoadFlowRunsPayload): Promise<StorageWorkerFlowRunsResult[]> {
    return this.request('loadFlowRuns', payload, 60000);
  }

  nukeStore(): Promise<true> {
    return this.request('nukeStore');
  }

  private async probeInternal(): Promise<StorageWorkerProbeResult | null> {
    if (typeof Worker === 'undefined') {
      return null;
    }

    try {
      const result = await this.request<StorageWorkerProbeResult>('probe', undefined, 30000);
      return result;
    } catch (error) {
      console.warn('[StorageWorkerClientService] storage worker probe failed', error);
      this.shutdown();
      return null;
    }
  }

  private ensureWorker(): Worker {
    if (this.worker) {
      return this.worker;
    }

    const worker = new Worker(new URL('./storage.worker', import.meta.url), { type: 'module' });
    worker.onmessage = ({ data }: MessageEvent<StorageWorkerMessage>) => this.handleMessage(data);
    worker.onerror = (event) => {
      console.warn('[StorageWorkerClientService] storage worker error', event.message);
    };
    this.worker = worker;
    return worker;
  }

  private request<TResult>(method: StorageWorkerRequest['method'], payload?: any, timeoutMs = 15000): Promise<TResult> {
    const worker = this.ensureWorker();
    const id = `storage-${Date.now()}-${++this.requestId}`;

    return new Promise<TResult>((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`Storage worker request timed out: ${method}`));
      }, timeoutMs);

      this.pending.set(id, { resolve, reject, timeout });
      worker.postMessage({ id, method, payload } satisfies StorageWorkerRequest);
    });
  }

  private handleMessage(message: StorageWorkerMessage): void {
    if ('event' in message) {
      if (message.event === 'ready') {
        console.log('[StorageWorkerClientService] storage worker ready', message.payload || {});
      }
      if (message.event === 'progress' || message.event === 'scan') {
        this.scanProgressSubject.next(message.payload as StorageWorkerScanProgressPayload);
      }
      return;
    }

    const response = message as StorageWorkerResponse;
    const pending = this.pending.get(response.id);
    if (!pending) {
      return;
    }

    this.pending.delete(response.id);
    clearTimeout(pending.timeout);

    if (response.ok) {
      pending.resolve(response.result);
      return;
    }

    pending.reject(new Error(response.error?.message || 'Storage worker request failed'));
  }
}
